import {useAtomValue} from 'jotai';
import {memo, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';
import {Typography, typographyStyle} from '@/components/Typography';
import {MEDALS} from '@/constants';
import {EstSalesTopRankItem} from '@/map/@types';
import {zoomLevelAtom} from '@/map/atom';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {Marker, Polygon} from '@/map/modules';
import {convertToCoordFromPointCoords, getGeometryCenterPoint, getZoomLevel} from '@/map/utils';
import {palette} from '@/styles';
import {humanizeNumber} from '@/utils';
import {MarkerContentContainer} from '../../Markers';

export interface IBlockProps {
  map: naver.maps.Map;
  activeId?: number;
  focusId?: number;

  onSelectItem?(id: number): void;
}

const HIDE_VALUE_ZOOM = getZoomLevel(300);

export const Block = memo(
  ({
    map,
    item,
    rank,
    activeId,
    focusId,
    onSelectItem,
  }: IBlockProps & {item: EstSalesTopRankItem; rank: number}) => {
    const zoomLevel = useAtomValue(zoomLevelAtom);

    const [hover, setHover] = useState<boolean>(false);
    const paths = useMemo<
      naver.maps.PolygonOptions['paths'] | undefined
    >(() => {
      if (item.geometry.coordinates?.[0]?.[0]) {
        return [
          item.geometry.coordinates[0][0].map(
            (i) => convertToCoordFromPointCoords(i) as naver.maps.LatLng
          ),
        ];
      }
    }, [item?.geometry]);

    const isActive = useMemo(
      () => rank === activeId || rank === focusId || hover,
      [activeId, focusId, hover, rank]
    );
    const hasMedal = useMemo(() => rank < MEDALS.length, [rank]);

    const markerPoint = useMemo(() => {
      if (item?.geometry) {
        return getGeometryCenterPoint(item.geometry.coordinates);
      }
    }, [item?.geometry]);

    const onMouseEvent = useCallback((e: any) => {
      setHover(e.originalEvent.type === 'mouseover');
    }, []);

    const onClick = useCallback(
      () => onSelectItem?.(rank),
      [rank, onSelectItem]
    );

    if (!item || !map || !paths) {
      return null;
    }

    return (
      <>
        <Polygon
          map={map}
          paths={paths}
          fillColor={palette.red100}
          zIndex={MAP_LAYER_Z_INDEX.RANKING_BLOCK}
          fillOpacity={0.1}
          clickable={true}
          strokeColor={palette.red100}
          strokeOpacity={isActive ? 1 : 0.7}
          strokeWeight={isActive ? 2 : 1}
          onClick={onClick}
          onMouseover={onMouseEvent}
          onMouseout={onMouseEvent}
        />
        {zoomLevel > HIDE_VALUE_ZOOM && !!markerPoint && (
          <Marker
            map={map}
            position={markerPoint}
            clickable={false}
            draggable={false}
            onClick={onClick}
          >
            <MarkerContentContainer
              anchor={{y: 9 + (hasMedal ? 8 : 0)}}
              style={{
                pointerEvents: 'none',
                zIndex: MAP_LAYER_Z_INDEX.RANKING_BLOCK,
              }}
            >
              <MarkerContent>
                {hasMedal && <Medal>{MEDALS[rank]}</Medal>}
                {!hasMedal && rank < 5 && (
                  <Typography color={palette.red200}>
                    {rank + 1}위&nbsp;
                  </Typography>
                )}
                <strong>{humanizeNumber(item.averageEstSale, true)}</strong>
              </MarkerContent>
            </MarkerContentContainer>
          </Marker>
        )}
      </>
    );
  }
);

const MarkerContent = styled.div`
  background-color: ${palette.red400};
  color: ${palette.white};
  ${{...typographyStyle.medium12}}
  white-space: nowrap;
  padding: 4px 8px;
  border-radius: 3px;
  position: relative;
`;

const Medal = styled.div`
  position: absolute;
  top: -28px;
  left: 50%;
  margin-left: -15px;
  font-size: 30px;
  line-height: 1;
`;
